import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Building2, Star, Plus, Edit2, Trash2, Check, X, Save, 
  ChevronLeft, ChevronRight, Home, Bed, Wifi, Tv, Coffee 
} from 'lucide-react';
import { auth } from '../../config/firebase';
import './HotelConfiguration.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
const API = `${API_BASE_URL}/manager/hotel`;

const HotelConfiguration = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [hotel, setHotel] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [roomTypeFacilities, setRoomTypeFacilities] = useState([]);
  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);

  const steps = [
    { id: 0, title: 'Hotel Details', icon: Building2 },
    { id: 1, title: 'Facilities', icon: Star },
    { id: 2, title: 'Room Types', icon: Bed },
    { id: 3, title: 'Room Type Facilities', icon: Wifi },
    { id: 4, title: 'Rooms & Floors', icon: Home },
    { id: 5, title: 'Final Review', icon: Check }
  ];

  const commonFacilities = [
    { name: 'Free WiFi', icon: Wifi, description: 'High-speed internet access' },
    { name: 'Parking', icon: '🅿️', description: 'On-site parking available' },
    { name: 'Restaurant', icon: Coffee, description: 'In-house dining' },
    { name: '24/7 Reception', icon: '🔔', description: 'Front desk always available' },
    { name: 'Room Service', icon: '🍽️', description: 'In-room dining service' },
    { name: 'Gym', icon: '💪', description: 'Fitness center access' },
    { name: 'Pool', icon: '🏊', description: 'Swimming pool' },
    { name: 'Spa', icon: '💆', description: 'Wellness and spa services' }
  ];

  useEffect(() => {
    fetchHotelData();
    fetchFacilities();
    fetchRoomTypes();
    fetchRoomTypeFacilities();
    fetchFloors();
    fetchRooms();
  }, []);

  const getAuthHeader = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Not authenticated');
    }
    const token = await currentUser.getIdToken();
    return { Authorization: `Bearer ${token}` };
  };

  const fetchHotelData = async () => {
    try {
      const headers = await getAuthHeader();
      const response = await axios.get(`${API}`, { headers });
      if (response.data.success) {
        setHotel(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching hotel:', error);
    }
  };

  const fetchFacilities = async () => {
    try {
      const headers = await getAuthHeader();
      const response = await axios.get(`${API}/facilities`, { headers });
      if (response.data.success) {
        setFacilities(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  };

  const fetchRoomTypes = async () => {
    try {
      const headers = await getAuthHeader();
      const response = await axios.get(`${API}/room-types`, { headers });
      if (response.data.success) {
        setRoomTypes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching room types:', error);
    }
  };

  const fetchRoomTypeFacilities = async () => {
    try {
      const headers = await getAuthHeader();
      const response = await axios.get(`${API}/room-type-facilities`, { headers });
      if (response.data.success) {
        setRoomTypeFacilities(response.data.data?.facilities || []);
      }
    } catch (error) {
      console.error('Error fetching room type facilities:', error);
    }
  };

  const fetchFloors = async () => {
    try {
      const headers = await getAuthHeader();
      const response = await axios.get(`${API}/floors`, { headers });
      if (response.data.success) {
        setFloors(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching floors:', error);
    }
  };

  const fetchRooms = async () => {
    try {
      const headers = await getAuthHeader();
      const response = await axios.get(`${API}/rooms`, { headers });
      if (response.data.success) {
        setRooms(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const addFacility = async (facility) => {
    try {
      const headers = await getAuthHeader();
      const response = await axios.post(`${API}/facilities`, facility, { headers });
      if (response.data.success) {
        fetchFacilities();
      }
    } catch (error) {
      console.error('Error adding facility:', error);
    }
  };

  const deleteFacility = async (id) => {
    if (!window.confirm('Are you sure you want to delete this facility?')) return;
    
    try {
      const headers = await getAuthHeader();
      await axios.delete(`${API}/facilities/${id}`, { headers });
      fetchFacilities();
    } catch (error) {
      console.error('Error deleting facility:', error);
    }
  };

  const addRoomType = async (roomType) => {
    try {
      const headers = await getAuthHeader();
      const response = await axios.post(`${API}/room-types`, roomType, { headers });
      if (response.data.success) {
        fetchRoomTypes();
      }
    } catch (error) {
      console.error('Error adding room type:', error);
    }
  };

  const deleteRoomType = async (id) => {
    if (!window.confirm('Are you sure you want to delete this room type?')) return;
    
    try {
      const headers = await getAuthHeader();
      await axios.delete(`${API}/room-types/${id}`, { headers });
      fetchRoomTypes();
    } catch (error) {
      console.error('Error deleting room type:', error);
    }
  };

  const addRoomTypeFacility = async (roomTypeId, facility) => {
    try {
      const headers = await getAuthHeader();
      const response = await axios.post(`${API}/room-type-facilities/${roomTypeId}`, facility, { headers });
      if (response.data.success) {
        fetchRoomTypeFacilities();
      }
    } catch (error) {
      console.error('Error adding room type facility:', error);
      alert(error.response?.data?.message || 'Failed to add facility');
    }
  };

  const updateRoomTypeFacility = async (roomTypeId, facilityId, facility) => {
    try {
      const headers = await getAuthHeader();
      const response = await axios.put(`${API}/room-type-facilities/${roomTypeId}/${facilityId}`, facility, { headers });
      if (response.data.success) {
        fetchRoomTypeFacilities();
      }
    } catch (error) {
      console.error('Error updating room type facility:', error);
      alert(error.response?.data?.message || 'Failed to update facility');
    }
  };

  const deleteRoomTypeFacility = async (roomTypeId, facilityId) => {
    if (!window.confirm('Are you sure you want to delete this facility?')) return;
    
    try {
      const headers = await getAuthHeader();
      await axios.delete(`${API}/room-type-facilities/${roomTypeId}/${facilityId}`, { headers });
      fetchRoomTypeFacilities();
    } catch (error) {
      console.error('Error deleting room type facility:', error);
      alert(error.response?.data?.message || 'Failed to delete facility');
    }
  };

  const bulkCreateRooms = async (floorId, roomsData) => {
    try {
      const headers = await getAuthHeader();
      const payload = {
        hotel_floor_id: floorId,
        rooms: roomsData.map(r => ({
          room_type_id: r.room_type_id,
          count: r.count
        }))
      };
      const response = await axios.post(`${API}/rooms/bulk`, payload, { headers });
      if (response.data.success) {
        fetchRooms();
        return true;
      }
    } catch (error) {
      console.error('Error creating rooms:', error);
      alert(error.response?.data?.message || 'Failed to create rooms');
      return false;
    }
  };

  const deleteRoom = async (roomId) => {
    if (!window.confirm('Are you sure you want to delete this room?')) return;
    
    try {
      const headers = await getAuthHeader();
      await axios.delete(`${API}/rooms/${roomId}`, { headers });
      fetchRooms();
    } catch (error) {
      console.error('Error deleting room:', error);
      alert(error.response?.data?.message || 'Failed to delete room');
    }
  };

  const syncSmartThings = async () => {
    try {
      const headers = await getAuthHeader();
      const response = await axios.post(`${API}/rooms/sync-smartthings`, {}, { headers });
      if (response.data.success) {
        fetchRooms();
        return response.data.data;
      }
    } catch (error) {
      console.error('Error syncing SmartThings:', error);
      throw error;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <HotelDetailsStep hotel={hotel} />;
      case 1:
        return <FacilitiesStep facilities={facilities} commonFacilities={commonFacilities} onAdd={addFacility} onDelete={deleteFacility} />;
      case 2:
        return <RoomTypesStep roomTypes={roomTypes} onAdd={addRoomType} onDelete={deleteRoomType} />;
      case 3:
        return <RoomTypeFacilitiesStep 
          roomTypes={roomTypes} 
          facilities={roomTypeFacilities} 
          onAdd={addRoomTypeFacility}
          onUpdate={updateRoomTypeFacility}
          onDelete={deleteRoomTypeFacility}
        />;
      case 4:
        return <RoomsFloorStep 
          floors={floors} 
          rooms={rooms} 
          roomTypes={roomTypes}
          hotel={hotel}
          onBulkCreate={bulkCreateRooms}
          onDeleteRoom={deleteRoom}
          onSyncSmartThings={syncSmartThings}
        />;
      case 5:
        return <FinalReviewStep hotel={hotel} facilities={facilities} roomTypes={roomTypes} roomTypeFacilities={roomTypeFacilities} rooms={rooms} />;
      default:
        return null;
    }
  };

  return (
    <div className="hotel-configuration">
      {/* Progress Steps */}
      <div className="configuration-steps">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={`step ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
            onClick={() => setCurrentStep(index)}
          >
            <div className="step-icon">
              {index < currentStep ? <Check size={20} /> : <step.icon size={20} />}
            </div>
            <span className="step-title">{step.title}</span>
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="step-content">
        {renderStepContent()}
      </div>

      {/* Navigation */}
      <div className="step-navigation">
        <button
          className="btn-secondary"
          onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
          disabled={currentStep === 0}
        >
          <ChevronLeft size={20} />
          Previous
        </button>
        <span className="step-counter">
          Step {currentStep + 1} of {steps.length}
        </span>
        <button
          className="btn-primary"
          onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
          disabled={currentStep === steps.length - 1}
        >
          Next
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

// Step 0: Hotel Details
const HotelDetailsStep = ({ hotel }) => {
  if (!hotel) {
    return <div className="loading-state">Loading hotel details...</div>;
  }

  return (
    <div className="step-panel">
      <h2>Hotel Details</h2>
      <p className="step-description">Review your hotel information below</p>
      
      <div className="hotel-details-preview">
        <div className="detail-row">
          <label>Hotel Name</label>
          <span>{hotel.name}</span>
        </div>
        <div className="detail-row">
          <label>Category</label>
          <span>{hotel.category}</span>
        </div>
        <div className="detail-row">
          <label>Address</label>
          <span>{hotel.address}, {hotel.city}, {hotel.state} {hotel.postal_code}</span>
        </div>
        <div className="detail-row">
          <label>Total Floors</label>
          <span>{hotel.total_floors}</span>
        </div>
        <div className="detail-row">
          <label>Total Rooms</label>
          <span>{hotel.total_rooms}</span>
        </div>
        <div className="detail-row">
          <label>Check-in / Check-out</label>
          <span>{hotel.check_in_time} / {hotel.check_out_time}</span>
        </div>
      </div>

      <div className="info-box">
        <p>✓ Hotel details are configured and ready</p>
      </div>
    </div>
  );
};

// Step 1: Facilities
const FacilitiesStep = ({ facilities, commonFacilities, onAdd, onDelete }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newFacility, setNewFacility] = useState({ name: '', description: '' });

  const handleAddCommonFacility = (facility) => {
    onAdd({ name: facility.name, description: facility.description, is_active: true });
  };

  const handleAddCustomFacility = () => {
    if (!newFacility.name) return;
    onAdd({ ...newFacility, is_active: true });
    setNewFacility({ name: '', description: '' });
    setShowAddForm(false);
  };

  return (
    <div className="step-panel">
      <h2>Hotel Facilities</h2>
      <p className="step-description">Add facilities available at your hotel</p>

      {/* Common Facilities */}
      <div className="common-facilities-section">
        <h3>Quick Add - Common Facilities</h3>
        <div className="facilities-carousel">
          {commonFacilities.map((facility, index) => {
            const alreadyAdded = facilities.some(f => f.name === facility.name);
            return (
              <div key={index} className={`facility-card ${alreadyAdded ? 'added' : ''}`}>
                <div className="facility-icon">{typeof facility.icon === 'string' ? facility.icon : <facility.icon size={24} />}</div>
                <h4>{facility.name}</h4>
                <p>{facility.description}</p>
                <button
                  className={alreadyAdded ? 'btn-added' : 'btn-add'}
                  onClick={() => !alreadyAdded && handleAddCommonFacility(facility)}
                  disabled={alreadyAdded}
                >
                  {alreadyAdded ? <><Check size={16} /> Added</> : <><Plus size={16} /> Add</>}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Custom Facilities */}
      <div className="custom-facilities-section">
        <div className="section-header">
          <h3>Your Hotel Facilities ({facilities.length})</h3>
          <button className="btn-primary" onClick={() => setShowAddForm(!showAddForm)}>
            <Plus size={18} /> Add Custom Facility
          </button>
        </div>

        {showAddForm && (
          <div className="add-facility-form">
            <input
              type="text"
              placeholder="Facility name"
              value={newFacility.name}
              onChange={(e) => setNewFacility({ ...newFacility, name: e.target.value })}
            />
            <input
              type="text"
              placeholder="Description"
              value={newFacility.description}
              onChange={(e) => setNewFacility({ ...newFacility, description: e.target.value })}
            />
            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setShowAddForm(false)}>
                <X size={16} /> Cancel
              </button>
              <button className="btn-primary" onClick={handleAddCustomFacility}>
                <Save size={16} /> Save
              </button>
            </div>
          </div>
        )}

        <div className="facilities-list">
          {facilities.map((facility) => (
            <div key={facility.id} className="facility-item">
              <div className="facility-info">
                <h4>{facility.name}</h4>
                <p>{facility.description}</p>
              </div>
              <button
                className="btn-delete"
                onClick={() => onDelete(facility.id)}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Step 2: Room Types
const RoomTypesStep = ({ roomTypes, onAdd, onDelete }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRoomType, setNewRoomType] = useState({
    room_type: '',
    max_adults: 2,
    max_children: 1,
    base_price: 0,
    description: ''
  });

  const handleAdd = () => {
    if (!newRoomType.room_type) return;
    onAdd(newRoomType);
    setNewRoomType({ room_type: '', max_adults: 2, max_children: 1, base_price: 0, description: '' });
    setShowAddForm(false);
  };

  return (
    <div className="step-panel">
      <h2>Room Types</h2>
      <p className="step-description">Define the types of rooms available in your hotel</p>

      <div className="section-header">
        <h3>Room Types ({roomTypes.length})</h3>
        <button className="btn-primary" onClick={() => setShowAddForm(!showAddForm)}>
          <Plus size={18} /> Add Room Type
        </button>
      </div>

      {showAddForm && (
        <div className="add-roomtype-form">
          <div className="form-group">
            <label>Room Type Name</label>
            <input
              type="text"
              placeholder="e.g., Deluxe Suite"
              value={newRoomType.room_type}
              onChange={(e) => setNewRoomType({ ...newRoomType, room_type: e.target.value })}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Max Adults</label>
              <input
                type="number"
                value={newRoomType.max_adults}
                onChange={(e) => setNewRoomType({ ...newRoomType, max_adults: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="form-group">
              <label>Max Children</label>
              <input
                type="number"
                value={newRoomType.max_children}
                onChange={(e) => setNewRoomType({ ...newRoomType, max_children: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="form-group">
              <label>Base Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={newRoomType.base_price}
                onChange={(e) => setNewRoomType({ ...newRoomType, base_price: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              placeholder="Room description..."
              value={newRoomType.description}
              onChange={(e) => setNewRoomType({ ...newRoomType, description: e.target.value })}
              rows={3}
            />
          </div>
          <div className="form-actions">
            <button className="btn-secondary" onClick={() => setShowAddForm(false)}>
              <X size={16} /> Cancel
            </button>
            <button className="btn-primary" onClick={handleAdd}>
              <Save size={16} /> Save
            </button>
          </div>
        </div>
      )}

      <div className="roomtypes-grid">
        {roomTypes.map((roomType) => (
          <div key={roomType.id} className="roomtype-card">
            <div className="roomtype-header">
              <h4>{roomType.room_type}</h4>
              <button className="btn-delete" onClick={() => onDelete(roomType.id)}>
                <Trash2 size={16} />
              </button>
            </div>
            <p className="roomtype-description">{roomType.description}</p>
            <div className="roomtype-details">
              <div className="detail-item">
                <span className="label">Capacity:</span>
                <span>{roomType.max_adults} adults, {roomType.max_children} children</span>
              </div>
              <div className="detail-item">
                <span className="label">Base Price:</span>
                <span className="price">${roomType.base_price}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Step 3: Room Type Facilities
const RoomTypeFacilitiesStep = ({ roomTypes, facilities, onAdd, onUpdate, onDelete }) => {
  const [selectedRoomType, setSelectedRoomType] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });

  const getFacilitiesForRoomType = (roomTypeId) => {
    return facilities.filter(f => f.hotel_room_type_id === roomTypeId);
  };

  const handleAddClick = (roomType) => {
    setSelectedRoomType(roomType);
    setEditingFacility(null);
    setFormData({ name: '', description: '' });
    setShowModal(true);
  };

  const handleEditClick = (roomType, facility) => {
    setSelectedRoomType(roomType);
    setEditingFacility(facility);
    setFormData({ name: facility.name, description: facility.description });
    setShowModal(true);
  };

  const handleSave = () => {
    if (!formData.name) {
      alert('Facility name is required');
      return;
    }

    if (editingFacility) {
      onUpdate(selectedRoomType.id, editingFacility.facility_id, formData);
    } else {
      onAdd(selectedRoomType.id, formData);
    }
    
    setShowModal(false);
    setFormData({ name: '', description: '' });
    setSelectedRoomType(null);
    setEditingFacility(null);
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormData({ name: '', description: '' });
    setSelectedRoomType(null);
    setEditingFacility(null);
  };

  return (
    <div className="step-panel">
      <h2>Room Type Facilities</h2>
      <p className="step-description">Assign specific amenities to each room type (e.g., King Bed, TV, Mini Bar)</p>

      <div className="room-type-facilities-container">
        {roomTypes.map(roomType => {
          const roomFacilities = getFacilitiesForRoomType(roomType.id);
          
          return (
            <div key={roomType.id} className="room-type-section">
              <div className="room-type-header">
                <h3>{roomType.room_type}</h3>
                <button 
                  className="btn-primary"
                  onClick={() => handleAddClick(roomType)}
                >
                  <Plus size={16} /> Add Facility
                </button>
              </div>

              {roomFacilities.length === 0 ? (
                <div className="no-facilities">
                  No facilities assigned to this room type yet.
                </div>
              ) : (
                <div className="facilities-grid">
                  {roomFacilities.map(facility => (
                    <div key={facility.facility_id} className="facility-card-simple">
                      <div className="facility-card-header">
                        <strong>{facility.name}</strong>
                      </div>
                      <p className="facility-description">{facility.description}</p>
                      <div className="facility-actions">
                        <button 
                          className="btn-edit"
                          onClick={() => handleEditClick(roomType, facility)}
                        >
                          <Edit2 size={14} /> Edit
                        </button>
                        <button 
                          className="btn-delete"
                          onClick={() => onDelete(roomType.id, facility.facility_id)}
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {roomTypes.length === 0 && (
          <div className="info-box">
            <p>No room types available. Please add room types in the previous step.</p>
          </div>
        )}
      </div>

      {/* Modal for Add/Edit Facility */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>{editingFacility ? 'Edit Facility' : 'Add Facility'}</h3>
            <p className="modal-subtitle">
              For: <strong>{selectedRoomType?.room_type}</strong>
            </p>
            
            <div className="form-group">
              <label>Facility Name *</label>
              <input
                type="text"
                placeholder="e.g., King Size Bed"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                placeholder="Describe this facility..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={handleCancel}>
                <X size={16} /> Cancel
              </button>
              <button className="btn-primary" onClick={handleSave}>
                <Save size={16} /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Step 4: Rooms & Floor Assignment
const RoomsFloorStep = ({ floors, rooms, roomTypes, hotel, onBulkCreate, onDeleteRoom, onSyncSmartThings }) => {
  const [selectedFloor, setSelectedFloor] = useState('');
  const [roomCounts, setRoomCounts] = useState({});
  const [expandedFloors, setExpandedFloors] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFloor, setFilterFloor] = useState('all');

  const getRoomFloorId = (room) => Number(room.hotel_floor_id ?? room.floor_id ?? room.floor);

  const totalRoomsAllowed = hotel?.total_rooms || 0;
  const totalExistingRooms = rooms.length;

  const toggleFloor = (floorId) => {
    setExpandedFloors(prev => ({
      ...prev,
      [floorId]: !prev[floorId]
    }));
  };

  const handleBulkCreate = async () => {
    if (!selectedFloor) {
      alert('Please select a floor');
      return;
    }

    const totalToAdd = Object.values(roomCounts).reduce((sum, val) => sum + Number(val || 0), 0);
    
    if (totalToAdd === 0) {
      alert('Please enter the number of rooms to add');
      return;
    }

    if (totalExistingRooms + totalToAdd > totalRoomsAllowed) {
      alert(`Cannot add more than ${totalRoomsAllowed} rooms in total`);
      return;
    }

    const roomsData = Object.entries(roomCounts)
      .filter(([_, count]) => Number(count) > 0)
      .map(([room_type_id, count]) => ({
        room_type_id: Number(room_type_id),
        count: Number(count)
      }));

    const success = await onBulkCreate(selectedFloor, roomsData);
    if (success) {
      setRoomCounts({});
      setSelectedFloor('');
    }
  };

  const handleSyncSmartThings = async () => {
    setSyncing(true);
    setSyncStatus(null);
    try {
      const result = await onSyncSmartThings();
      setSyncStatus({
        type: 'success',
        message: `Sync completed: ${result.synced || 0} synced, ${result.created || 0} created`
      });
    } catch (error) {
      console.error('SmartThings sync error:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.error 
        || error.response?.data?.message 
        || error.message 
        || 'Failed to sync with SmartThings';
      
      setSyncStatus({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setSyncing(false);
    }
  };

  const getRoomsForFloor = (floorId) => {
    return rooms.filter(r => getRoomFloorId(r) === Number(floorId));
  };

  const getRoomTypeName = (roomTypeId) => {
    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    return roomType?.room_type || 'Unknown';
  };

  const filteredRooms = rooms.filter(room => {
    const matchesSearch = searchTerm === '' || 
      room.room_number.toString().includes(searchTerm) ||
      getRoomTypeName(room.room_type_id).toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFloor = filterFloor === 'all' || getRoomFloorId(room) === Number(filterFloor);
    return matchesSearch && matchesFloor;
  });

  const totalToAdd = Object.values(roomCounts).reduce((sum, val) => sum + Number(val || 0), 0);
  const disableAddRooms = !selectedFloor || totalToAdd === 0 || (totalExistingRooms + totalToAdd > totalRoomsAllowed);

  return (
    <div className="step-panel">
      <h2>Rooms & Floor Assignment</h2>
      <p className="step-description">Assign room numbers to floors and sync with SmartThings IoT platform</p>

      {/* Bulk Create Section */}
      <div className="bulk-create-section">
        <h3>Add Rooms to Floor</h3>
        
        <div className="bulk-create-form">
          <div className="form-group">
            <label>Select Floor</label>
            <select 
              value={selectedFloor} 
              onChange={e => setSelectedFloor(e.target.value)}
              className="floor-select"
            >
              <option value="">Choose a floor...</option>
              {floors.map(floor => (
                <option key={floor.id} value={floor.id}>
                  Floor {floor.floor_id}
                </option>
              ))}
            </select>
          </div>

          <div className="room-type-counts">
            {roomTypes.map(roomType => (
              <div key={roomType.id} className="room-type-input">
                <label>{roomType.room_type}</label>
                <input
                  type="number"
                  min="0"
                  value={roomCounts[roomType.id] || ''}
                  onChange={e => setRoomCounts({ ...roomCounts, [roomType.id]: e.target.value })}
                  placeholder="Count"
                />
              </div>
            ))}
          </div>

          <div className="bulk-create-info">
            <div className="room-count-summary">
              <span>Total Existing: <strong>{totalExistingRooms}</strong></span>
              <span>To Add: <strong>{totalToAdd}</strong></span>
              <span>Limit: <strong>{totalRoomsAllowed}</strong></span>
            </div>
            <button 
              className="btn-primary"
              onClick={handleBulkCreate}
              disabled={disableAddRooms}
            >
              <Plus size={16} /> Add Rooms
            </button>
          </div>
        </div>
      </div>

      {/* SmartThings Sync Section */}
      <div className="smartthings-section">
        <div className="section-header">
          <h3>SmartThings Integration</h3>
          <button
            className="btn-sync"
            onClick={handleSyncSmartThings}
            disabled={syncing || rooms.length === 0}
            title={rooms.length === 0 ? 'Add rooms first before syncing' : 'Sync rooms with SmartThings'}
          >
            {syncing ? (
              <>🔄 Syncing...</>
            ) : (
              <>🔗 Sync SmartThings</>
            )}
          </button>
        </div>

        {syncStatus && (
          <div className={`sync-status ${syncStatus.type}`}>
            <span>{syncStatus.type === 'success' ? '✅' : '❌'}</span>
            <div style={{ flex: 1 }}>
              <div><strong>{syncStatus.message}</strong></div>
              {syncStatus.type === 'error' && (
                <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.95 }}>
                  {syncStatus.message.includes('ECONNRESET') || syncStatus.message.includes('Cannot connect') ? (
                    <>
                      <div style={{ marginBottom: '6px' }}>
                        <strong>💡 This error usually means:</strong>
                      </div>
                      <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        <li>Your SmartThings token (SMARTTHINGS_PAT) is invalid or expired</li>
                        <li>The SmartThings API is temporarily unavailable</li>
                      </ul>
                      <div style={{ marginTop: '8px' }}>
                        <strong>🔧 To fix:</strong>
                        <ol style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                          <li>Check backend console logs for detailed error</li>
                          <li>Verify SMARTTHINGS_PAT in backend/.env file</li>
                          <li>Get a new token from <a href="https://account.smartthings.com/tokens" target="_blank" rel="noopener" style={{ color: 'white', textDecoration: 'underline' }}>SmartThings</a></li>
                        </ol>
                      </div>
                    </>
                  ) : (
                    <div>💡 Tip: Check backend console logs for detailed error information</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <p className="sync-description">
          Sync your rooms with SmartThings IoT platform to enable smart room controls, 
          automated lighting, temperature management, and occupancy detection.
          {rooms.length === 0 && <strong> Please add rooms first before syncing.</strong>}
        </p>
      </div>

      {/* Existing Rooms Section */}
      <div className="existing-rooms-section">
        <div className="section-header">
          <h3>Existing Rooms ({rooms.length})</h3>
          <div className="room-filters">
            <input
              type="text"
              placeholder="🔍 Search rooms..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <select 
              value={filterFloor} 
              onChange={e => setFilterFloor(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Floors</option>
              {floors.map(f => (
                <option key={f.id} value={f.id}>Floor {f.floor_id}</option>
              ))}
            </select>
          </div>
        </div>

        {floors.length === 0 ? (
          <div className="info-box">
            <p>No floors available. Floors are automatically created based on your hotel's total floors.</p>
          </div>
        ) : (
          <div className="floors-container">
            {floors.map(floor => {
              const floorRooms = filteredRooms.filter(r => getRoomFloorId(r) === Number(floor.id));
              const isExpanded = expandedFloors[floor.id] !== false; // Default to expanded

              return (
                <div key={floor.id} className="floor-section">
                  <div 
                    className="floor-header"
                    onClick={() => toggleFloor(floor.id)}
                  >
                    <div className="floor-info">
                      <span className="floor-icon">{isExpanded ? '▼' : '▶'}</span>
                      <h4>Floor {floor.floor_id}</h4>
                      <span className="room-count-badge">{floorRooms.length} rooms</span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="floor-rooms">
                      {floorRooms.length === 0 ? (
                        <div className="no-rooms">No rooms on this floor yet</div>
                      ) : (
                        <div className="rooms-grid">
                          {floorRooms.map(room => (
                            <div key={room.id} className="room-card">
                              <div className="room-header">
                                <span className="room-number">Room {room.room_number}</span>
                                <span className={`sync-badge ${room.smartthings_sync_status}`}>
                                  {room.smartthings_sync_status === 'synced' ? '🔗' : '⚠️'}
                                </span>
                              </div>
                              <div className="room-details">
                                <div className="room-type">{getRoomTypeName(room.room_type_id)}</div>
                                <div className="room-status">
                                  <span className={`status-badge ${room.status}`}>
                                    {room.status || 'available'}
                                  </span>
                                </div>
                              </div>
                              <button
                                className="btn-delete-room"
                                onClick={() => onDeleteRoom(room.id)}
                              >
                                <Trash2 size={14} /> Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {rooms.length === 0 && (
          <div className="info-box">
            <p>No rooms created yet. Use the form above to add rooms to your floors.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Step 5: Final Review
const FinalReviewStep = ({ hotel, facilities, roomTypes, roomTypeFacilities, rooms }) => {
  const [activating, setActivating] = useState(false);

  const handleActivate = async () => {
    if (!window.confirm('Are you sure you want to activate your hotel? This will make it available for guest bookings.')) {
      return;
    }

    try {
      setActivating(true);
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Not authenticated');
      }
      const token = await currentUser.getIdToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api'}/manager/hotel/activate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert('Hotel activated successfully! You can now receive guest bookings.');
        window.location.reload();
      }
    } catch (error) {
      console.error('Error activating hotel:', error);
      alert('Failed to activate hotel. Please try again.');
    } finally {
      setActivating(false);
    }
  };

  const checklistItems = [
    { label: 'Hotel Details', completed: !!hotel, count: hotel ? 1 : 0 },
    { label: 'Facilities', completed: facilities.length > 0, count: facilities.length },
    { label: 'Room Types', completed: roomTypes.length > 0, count: roomTypes.length },
    { label: 'Room Type Facilities', completed: roomTypeFacilities.length > 0, count: roomTypeFacilities.length },
    { label: 'Rooms & Floors', completed: rooms.length > 0, count: rooms.length },
    { label: 'SmartThings Integration', completed: rooms.some(r => r.smartthings_sync_status === 'synced'), count: rooms.filter(r => r.smartthings_sync_status === 'synced').length }
  ];

  const allCompleted = checklistItems.every(item => item.completed);

  return (
    <div className="step-panel">
      <h2>Final Review</h2>
      <p className="step-description">Review your hotel configuration before activation</p>

      <div className="review-checklist">
        <h3>Configuration Checklist</h3>
        {checklistItems.map((item, index) => (
          <div key={index} className={`checklist-item ${item.completed ? 'completed' : 'incomplete'}`}>
            <div className="check-icon">
              {item.completed ? <Check size={20} /> : <X size={20} />}
            </div>
            <div className="checklist-content">
              <span className="checklist-label">{item.label}</span>
              {item.count > 0 && <span className="checklist-count">({item.count} configured)</span>}
            </div>
          </div>
        ))}
      </div>

      {allCompleted ? (
        <div className="activation-section">
          <div className="success-message">
            <Check size={48} />
            <h3>All Set!</h3>
            <p>Your hotel configuration is complete. Click below to activate and start receiving guests.</p>
          </div>
          <button
            className="btn-activate"
            onClick={handleActivate}
            disabled={activating}
          >
            {activating ? 'Activating...' : 'Activate Hotel'}
          </button>
        </div>
      ) : (
        <div className="warning-message">
          <p>⚠️ Please complete all required steps before activating your hotel.</p>
        </div>
      )}
    </div>
  );
};

export default HotelConfiguration;
